import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Line, ComposedChart } from 'recharts';
import { ArrowLeft, Printer, TrendingUp, TrendingDown, Building2, Car, Warehouse, Scale, ShoppingCart } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(v || 0);

const typeLabels = {
  mecanica: 'Mecânica', pecas: 'Peças', legalizacao: 'Legalização',
  transporte: 'Transporte', limpeza: 'Limpeza', outros: 'Outros',
  renda: 'Renda', eletricidade: 'Eletricidade', internet: 'Internet',
  salarios: 'Salários', contabilidade: 'Contabilidade', seguros: 'Seguros',
  publicidade: 'Publicidade', manutencao: 'Manutenção'
};

// Print component
const ReportPrint = React.forwardRef(({ data }, ref) => {
  if (!data) return null;
  return (
    <div ref={ref} style={{ width: '210mm', padding: '15mm', background: 'white', color: 'black', fontFamily: 'Arial, sans-serif', fontSize: '11px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '5mm', borderBottom: '2px solid #DC2626', marginBottom: '6mm' }}>
        <div>
          <div style={{ fontSize: '22px', fontWeight: '800', color: '#DC2626' }}>ICARDETAIL</div>
          <div style={{ fontSize: '9px', color: '#666', letterSpacing: '2px' }}>RELATÓRIO ANUAL</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '22px', fontWeight: '700' }}>{data.year_label}</div>
          <div style={{ fontSize: '9px', color: '#666' }}>Gerado em {new Date().toLocaleDateString('pt-PT')}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '4mm', marginBottom: '6mm' }}>
        <div style={{ background: '#f5f5f5', padding: '4mm' }}>
          <div style={{ fontSize: '9px', color: '#888' }}>RECEITA VENDAS</div>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>{fmt(data.total_revenue)}</div>
          <div style={{ fontSize: '9px', color: '#888' }}>{data.sold_count} vendidas</div>
        </div>
        <div style={{ background: '#f5f5f5', padding: '4mm' }}>
          <div style={{ fontSize: '9px', color: '#888' }}>DESPESAS TOTAIS</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: '#DC2626' }}>{fmt(data.total_expenses)}</div>
        </div>
        <div style={{ background: '#f5f5f5', padding: '4mm' }}>
          <div style={{ fontSize: '9px', color: '#888' }}>STOCK ATUAL</div>
          <div style={{ fontSize: '14px', fontWeight: '600' }}>{fmt(data.stock_value)}</div>
          <div style={{ fontSize: '9px', color: '#888' }}>{data.stock_count} viaturas</div>
        </div>
        <div style={{ background: data.net_profit >= 0 ? '#ecfdf5' : '#fef2f2', padding: '4mm' }}>
          <div style={{ fontSize: '9px', color: '#888' }}>RESULTADO</div>
          <div style={{ fontSize: '16px', fontWeight: '700', color: data.net_profit >= 0 ? '#10B981' : '#DC2626' }}>{fmt(data.net_profit)}</div>
        </div>
      </div>

      {data.sold_vehicles?.length > 0 && (
        <div style={{ marginBottom: '6mm' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '3mm', borderBottom: '1px solid #ddd', paddingBottom: '2mm' }}>
            VIATURAS VENDIDAS ({data.sold_count})
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #ddd' }}>
                <th style={{ textAlign: 'left', padding: '2mm', color: '#888', fontSize: '9px' }}>Viatura</th>
                <th style={{ textAlign: 'right', padding: '2mm', color: '#888', fontSize: '9px' }}>Compra</th>
                <th style={{ textAlign: 'right', padding: '2mm', color: '#888', fontSize: '9px' }}>Despesas</th>
                <th style={{ textAlign: 'right', padding: '2mm', color: '#888', fontSize: '9px' }}>Venda</th>
                <th style={{ textAlign: 'right', padding: '2mm', color: '#888', fontSize: '9px' }}>Lucro</th>
                <th style={{ textAlign: 'right', padding: '2mm', color: '#888', fontSize: '9px' }}>Margem</th>
              </tr>
            </thead>
            <tbody>
              {data.sold_vehicles.map((v, i) => (
                <tr key={v.id || `row-${i}`} style={{ borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '2mm' }}>{v.label}</td>
                  <td style={{ textAlign: 'right', padding: '2mm' }}>{fmt(v.purchase_price)}</td>
                  <td style={{ textAlign: 'right', padding: '2mm' }}>{fmt(v.expenses)}</td>
                  <td style={{ textAlign: 'right', padding: '2mm' }}>{fmt(v.sale_price)}</td>
                  <td style={{ textAlign: 'right', padding: '2mm', fontWeight: '600', color: v.profit >= 0 ? '#10B981' : '#DC2626' }}>{fmt(v.profit)}</td>
                  <td style={{ textAlign: 'right', padding: '2mm' }}>{v.margin}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {Object.keys(data.general_by_type || {}).length > 0 && (
        <div style={{ marginBottom: '6mm' }}>
          <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '3mm', borderBottom: '1px solid #ddd', paddingBottom: '2mm' }}>
            DESPESAS GERAIS ({fmt(data.total_general_expenses)})
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3mm' }}>
            {Object.entries(data.general_by_type).map(([type, amount]) => (
              <div key={type}><span style={{ color: '#888' }}>{typeLabels[type] || type}: </span><span style={{ fontWeight: '600' }}>{fmt(amount)}</span></div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly summary table */}
      <div style={{ marginBottom: '6mm' }}>
        <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '3mm', borderBottom: '1px solid #ddd', paddingBottom: '2mm' }}>
          RESUMO MENSAL
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ddd' }}>
              <th style={{ textAlign: 'left', padding: '2mm', color: '#888', fontSize: '9px' }}>Mês</th>
              <th style={{ textAlign: 'right', padding: '2mm', color: '#888', fontSize: '9px' }}>Vendas</th>
              <th style={{ textAlign: 'right', padding: '2mm', color: '#888', fontSize: '9px' }}>Receita</th>
              <th style={{ textAlign: 'right', padding: '2mm', color: '#888', fontSize: '9px' }}>Despesas</th>
              <th style={{ textAlign: 'right', padding: '2mm', color: '#888', fontSize: '9px' }}>Resultado</th>
            </tr>
          </thead>
          <tbody>
            {data.monthly_data?.filter(m => m.revenue > 0 || m.total_expenses > 0).map((m, i) => (
              <tr key={m.month || `month-${i}`} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '2mm' }}>{m.month}</td>
                <td style={{ textAlign: 'right', padding: '2mm' }}>{m.sold_count}</td>
                <td style={{ textAlign: 'right', padding: '2mm' }}>{fmt(m.revenue)}</td>
                <td style={{ textAlign: 'right', padding: '2mm', color: '#DC2626' }}>{fmt(m.total_expenses)}</td>
                <td style={{ textAlign: 'right', padding: '2mm', fontWeight: '600', color: m.profit >= 0 ? '#10B981' : '#DC2626' }}>{fmt(m.profit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: '8mm', paddingTop: '3mm', borderTop: '1px solid #ddd', fontSize: '9px', color: '#888', display: 'flex', justifyContent: 'space-between' }}>
        <span>icardetail.pt | 919 714 805</span>
        <span>Documento interno - Uso confidencial</span>
      </div>
    </div>
  );
});
ReportPrint.displayName = 'ReportPrint';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1c2a] border border-zinc-700 px-4 py-3 text-sm">
      <p className="text-white font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={p.dataKey || p.name || i} style={{ color: p.color }}>{p.name}: {fmt(p.value)}</p>
      ))}
    </div>
  );
};

const AdminAnnualReport = () => {
  const navigate = useNavigate();
  const printRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(String(new Date().getFullYear()));

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/finance/report/annual?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const reactToPrintFn = useReactToPrint({ contentRef: printRef, documentTitle: data ? `Relatorio_Anual_${data.year_label}` : 'Relatorio' });

  const getYearOptions = () => {
    const current = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => ({ value: String(current - i), label: String(current - i) }));
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div style={{ position: 'absolute', left: '-9999px', top: 0 }}><ReportPrint ref={printRef} data={data} /></div>

        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/admin/financas')} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors" data-testid="back-to-finance">
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-medium text-white" data-testid="annual-report-title">Relatório Anual</h1>
            <p className="text-zinc-500">{data?.year_label || 'A carregar...'}</p>
          </div>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-32 bg-[#12141D] border-zinc-800 text-white" data-testid="report-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#12141D] border-zinc-800">
              {getYearOptions().map(o => (
                <SelectItem key={o.value} value={o.value} className="text-white hover:bg-zinc-800">{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => reactToPrintFn()} disabled={!data} className="bg-red-600 hover:bg-red-700 text-white" data-testid="export-annual-btn">
            <Printer size={18} className="mr-2" /> Exportar PDF
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-20"><p className="text-zinc-400">A gerar relatório anual...</p></div>
        ) : data && (
          <>
            {/* KPI Row */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8" data-testid="annual-kpis">
              <div className="bg-[#12141D] border border-zinc-800 p-5">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-zinc-500 text-sm">Receita Vendas</p>
                  <TrendingUp size={18} className="text-emerald-500" />
                </div>
                <p className="text-2xl font-semibold text-white">{fmt(data.total_revenue)}</p>
                <p className="text-zinc-600 text-xs mt-1">{data.sold_count} viatura(s) vendida(s)</p>
              </div>
              <div className="bg-[#12141D] border border-zinc-800 p-5">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-zinc-500 text-sm">Despesas Totais</p>
                  <TrendingDown size={18} className="text-red-500" />
                </div>
                <p className="text-2xl font-semibold text-red-400">{fmt(data.total_expenses)}</p>
                <p className="text-zinc-600 text-xs mt-1">{data.vehicle_expense_count + data.general_expense_count} lançamentos</p>
              </div>
              <div className="bg-[#12141D] border border-zinc-800 p-5">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-zinc-500 text-sm">Compras</p>
                  <ShoppingCart size={18} className="text-blue-500" />
                </div>
                <p className="text-2xl font-semibold text-blue-400">{fmt(data.total_added_cost)}</p>
                <p className="text-zinc-600 text-xs mt-1">{data.cars_added} viatura(s) adquirida(s)</p>
              </div>
              <div className="bg-[#12141D] border border-zinc-800 p-5">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-zinc-500 text-sm">Stock Atual</p>
                  <Warehouse size={18} className="text-blue-500" />
                </div>
                <p className="text-2xl font-semibold text-blue-400">{fmt(data.stock_value)}</p>
                <p className="text-zinc-600 text-xs mt-1">{data.stock_count} no stand (custo: {fmt(data.stock_invested)})</p>
              </div>
              <div className={`bg-[#12141D] border p-5 ${data.net_profit >= 0 ? 'border-emerald-600/30' : 'border-red-600/30'}`}>
                <div className="flex items-start justify-between mb-2">
                  <p className="text-zinc-500 text-sm">Resultado Anual</p>
                  {data.net_profit >= 0 ? <TrendingUp size={18} className="text-emerald-500" /> : <TrendingDown size={18} className="text-red-500" />}
                </div>
                <p className={`text-2xl font-semibold ${data.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmt(data.net_profit)}</p>
                <p className="text-zinc-600 text-xs mt-1">Receita - Despesas</p>
              </div>
            </div>

            {/* Monthly Chart */}
            <div className="bg-[#12141D] border border-zinc-800 p-6 mb-8" data-testid="annual-chart">
              <h3 className="text-white font-medium mb-6">Evolução Mensal {data.year_label}</h3>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data.monthly_data} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="month" tick={{ fill: '#71717a', fontSize: 12 }} />
                    <YAxis tick={{ fill: '#71717a', fontSize: 12 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="revenue" name="Receita" fill="#10b981" radius={[4,4,0,0]} />
                    <Bar dataKey="vehicle_expenses" name="Desp. Viaturas" fill="#DC2626" radius={[4,4,0,0]} />
                    <Bar dataKey="general_expenses" name="Desp. Gerais" fill="#f97316" radius={[4,4,0,0]} />
                    <Line type="monotone" dataKey="profit" name="Resultado" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Two columns */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Sold Vehicles */}
              <div className="bg-[#12141D] border border-zinc-800 overflow-hidden" data-testid="annual-sold-vehicles">
                <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
                  <Car size={18} className="text-red-500" />
                  <h3 className="text-white font-medium">Viaturas Vendidas</h3>
                  <span className="ml-auto text-zinc-500 text-sm">{data.sold_count}</span>
                </div>
                {data.sold_vehicles?.length === 0 ? (
                  <div className="py-8 text-center text-zinc-500 text-sm">Nenhuma viatura vendida em {data.year_label}</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-800 text-zinc-500">
                        <th className="text-left px-5 py-2 font-medium">Viatura</th>
                        <th className="text-right px-3 py-2 font-medium">Compra</th>
                        <th className="text-right px-3 py-2 font-medium">Venda</th>
                        <th className="text-right px-3 py-2 font-medium">Lucro</th>
                        <th className="text-right px-4 py-2 font-medium">Margem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sold_vehicles.map((v, i) => (
                        <tr key={v.id || `row-${i}`} className="border-b border-zinc-800/50">
                          <td className="px-5 py-2 text-white">{v.label}</td>
                          <td className="text-right px-3 py-2 text-zinc-400">{fmt(v.purchase_price)}</td>
                          <td className="text-right px-3 py-2 text-zinc-300">{fmt(v.sale_price)}</td>
                          <td className={`text-right px-3 py-2 font-medium ${v.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{fmt(v.profit)}</td>
                          <td className={`text-right px-4 py-2 ${v.margin >= 10 ? 'text-emerald-400' : v.margin >= 0 ? 'text-yellow-400' : 'text-red-400'}`}>{v.margin}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* General Expenses */}
              <div className="bg-[#12141D] border border-zinc-800 overflow-hidden" data-testid="annual-general-expenses">
                <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
                  <Building2 size={18} className="text-orange-500" />
                  <h3 className="text-white font-medium">Despesas Gerais</h3>
                  <span className="ml-auto text-orange-400 font-medium">{fmt(data.total_general_expenses)}</span>
                </div>
                {Object.keys(data.general_by_type || {}).length === 0 ? (
                  <div className="py-8 text-center text-zinc-500 text-sm">Nenhuma despesa geral em {data.year_label}</div>
                ) : (
                  <div className="p-5 space-y-3">
                    {Object.entries(data.general_by_type).sort((a, b) => b[1] - a[1]).map(([type, amount]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-zinc-300">{typeLabels[type] || type}</span>
                        <span className="text-orange-400 font-medium">{fmt(amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Vehicle Expenses */}
            {data.vehicle_summaries?.length > 0 && (
              <div className="bg-[#12141D] border border-zinc-800 overflow-hidden" data-testid="annual-vehicle-expenses">
                <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
                  <Car size={18} className="text-red-500" />
                  <h3 className="text-white font-medium">Despesas por Viatura</h3>
                  <span className="ml-auto text-red-400 font-medium">{fmt(data.total_vehicle_expenses)}</span>
                </div>
                <div className="divide-y divide-zinc-800/50">
                  {data.vehicle_summaries.map((v, i) => (
                    <div key={v.id || `exp-${i}`} className="px-5 py-3 flex items-center justify-between hover:bg-white/[0.02]">
                      <div>
                        <span className="text-white font-medium">{v.label}</span>
                        <span className="text-zinc-500 text-xs ml-2">{v.items.length} despesa(s)</span>
                      </div>
                      <span className="text-red-400 font-medium">{fmt(v.total)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAnnualReport;
